<template>
  <zm-form ref="zmoneForm" :label-position="'top'" class="opc-config-form opc-msq-config-form" @submit.prevent :class="{ 'opc-config-form__multiple-meeting': isMultipleMode, 'hide-bottom-border-last-item': !canAddMore }">
    <zm-form-item class="opc-zmone__license-row">
      <zm-row>
        <zm-col :span="6">
          <p class="opc-zmone__form-label">
            <span v-if="isCartBuilderAddQty">{{ $t('billing.delta.add_user') }}</span>
            <span v-else>{{ $t('billing.simplify.select_license') }}</span>
            <zm-tooltip v-if="!isCartBuilderAddQty"  class="item opc-zmone__numbers-users-tooltip"
              disallow-learn-about
              aria-label="Learn more about Select the number of users"
              placement="top">
              <!-- <i class="zm-icon-info-outline main-icon" role="button"></i> -->
              <tips-icon type="info" aria-label="Learn more about Select the number of users" />
              <template v-slot:content>
                {{ $t('billing.zone.attendee_info', [attendee]) }}
                <zm-button data-testid="checkout-button-129" v-if="ctx.showIncreaseWithLargeMeetingPromo" @click="addLargeMeetingCard" class="opc-zmone__tip-lm-btn" type="link">{{$t('billing.large_meeting.increase_with')}}</zm-button>
              </template>
            </zm-tooltip>
          </p>
        </zm-col>
        <zm-col :span="canRemove ? 5 : 6">
          <InputNumberWithPopover class="opc-zmone__quantity-selector" :class="{ 'move-to-right': !msqReadOnlyCycle}" v-model="form.selectedNumber" size="lg"
            controls-position="both" :min="ctx.minHost" :max="ctx.maxHost" @change="updatePlanLicense"
            :decrease-tips="ctx.decreaseTips" :increase-tips="ctx.increaseTips" :aria-label="`${ctx.title} ${billingCycleLabel} Select the number of users`"
            :data-testid="`checkout-input-license-${plan ? plan.plan_code : ''}`" />
        </zm-col>
        <zm-col :span="1" v-if="canRemove" class="opc-zmone__license-row-right-btn-container">
          <zm-tooltip v-if="removeDisabledReason">
            <template #content>
              <div v-html="removeDisabledReason" />
            </template>
            <zm-button
              :icon="ManualCloseMark"
              class="opc-zmone__license-row-right-btn"
              aria-disabled="true"
              disabled
              :aria-label="`Remove ${ctx.title} ${billingCycleLabel} subscription`"
              type="text"
              @click="removeThisPlan"
              size="lg" />
          </zm-tooltip>
          <zm-button
            v-else
            :icon="ManualCloseMark"
            class="opc-zmone__license-row-right-btn"
            type="text"
            :aria-label="`Remove ${ctx.title} ${billingCycleLabel} subscription`"
            @click="removeThisPlan"
            size="lg" />
        </zm-col>
      </zm-row>
      <template v-if="!ctx.isCartBuilderAddQty">
        <license-tip v-if="ctx.isNormalCase" :type="plan.category" :number="form.selectedNumber" :additionalSpans='ctx.additionalSpans'>
          <zm-button data-testid="checkout-button-130" v-if="ctx.showExploreLink" class="opc-zmone__link" type="link"
            @click="ctx.handleExploreOtherPlansClick">{{ $t('billing.zone.explore_other_plans') }}</zm-button>
        </license-tip>
      </template>
    </zm-form-item>
    <zm-form-item v-if="msqReadOnlyCycle">
      <zm-row>
        <zm-col :span="6">
          <p class="opc-zmone__form-label">{{ $t('billing.simplify.subscription_cycle') }}</p>
        </zm-col>
        <zm-col :span="6">
          <div class="msq-readonly-cycle-content">
            <p class="msq-readonly-cycle-content__title text-capitalize">{{ $t(`billing.billing_cycle.${plan.billing_cycle}`) }}</p>
            <p class="msq-readonly-cycle-content__price">
              <span>{{ getMonthlyPrice(plan.amount, plan.cycleValue, { forceDiff: true }) }}</span>
              <span v-html='getMergedBilledText(plan.billing_cycle, plan.amount)'></span><priceVATTip />
            </p>
          </div>
        </zm-col>
      </zm-row>
    </zm-form-item>
    <zm-form-item v-else :label="$t('billing.simplify.subscription_cycle')">
      <cycle-radio-group
        v-model="form.billingCycle"
        class="opc-card-radio"
        :plans="ctx.currentFilterPlans"
        :action="switchBillingCycle"
        :aria-label="`${ctx.title} Billing Cycle`"
      >
        <zm-tooltip v-for="planItem in ctx.currentFilterPlans" :key="planItem.billing_cycle"
          :disabled="!(typeof cycleDisabled(planItem) === 'string')">
          <template #content>
            <div v-html="cycleDisabled(planItem) || ''" />
          </template>
          <zm-radio :data-testid="`checkout-radio-billing-cycle-${planItem.plan_code}`" :label="planItem.billing_cycle" :disabled="!!cycleDisabled(planItem)"
            class="opc-zmone__radio">
            <p class="opc-zmone__radio-title">
              {{ $t(`billing.billing_cycle.${planItem.billing_cycle}`)}}
            </p>
            <subscription-cycle-sub-title :billingCycle="planItem.billing_cycle" :currentCategory="planItem.category" />
            <p class="opc-product-price-container" tabindex="-1" :aria-label="ctx.ariaPriceOfMonthForLabel(planItem)">
              <span class="opc-product-price-container__price" aria-hidden="true">{{ ctx.getFormattedPriceOfMonth(planItem, { forceDiff: true }) }}</span>
            </p>
            <p class="opc-zmone__radio-price-unit opc-price-bill-text">
              <span v-html="ctx.getMergedBilledText(planItem.billing_cycle, planItem.amount)"></span><priceVATTip /><span v-if="ctx.showAnnualTermMonthly">*</span>
            </p>
          </zm-radio>
        </zm-tooltip>
      </cycle-radio-group>
    </zm-form-item>
    <PriceBillTextTips v-if="ctx.showAnnualTermMonthly" />
  </zm-form>
</template>
<script>
import { BILLING_CYCLE, planTypes } from '@/common/constants';
import { mapMutations, mapGetters, mapActions } from 'vuex';
import PriceBillTextTips from '@/plans/ZOOM_MEETING/components/price-bill-text-tips.vue';
import InputNumberWithPopover from '@/components/input-number/input-number-with-popover';
import { isCartBuilderAddQty } from '@/utils/cart-builder';
import { throttle, cloneDeep } from 'lodash';
import SubscriptionCycleSubTitle from '@/plans/ZOOM_MEETING/components/subscription-cycle-sub-title.vue';
import discountedMonthPrice from '@/common/mixin/discounted-month-price';
import { ManualCloseMark } from '@zoom/icons-vue';
import BigJs from 'big.js';
import LicenseTip from '../license-tip.vue';

export default {
  name: 'MeetingForm',
  inject: ['$core'],
  mixins: [discountedMonthPrice],
  setup() {
    return {
      ManualCloseMark,
    }
  },
  components: {
    PriceBillTextTips,
    InputNumberWithPopover,
    SubscriptionCycleSubTitle,
    LicenseTip,
  },
  props: {
    ctx: {
      type: Object,
      required: true,
    },
    plan: {
      type: Object,
      required: true,
    },
    canRemove: {
      type: Boolean,
      default: false,
    },
    isMultipleMode: {
      type: Boolean,
      default: false,
    },
    canAddMore: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      attendeeInfoTooltip: false,
      isCartBuilderAddQty,
      form: {
        selectedNumber: 0,
        billingCycle: BILLING_CYCLE.MONTHLY,
      }
    }
  },
  computed: {
    ...mapGetters(['getSelectedPlans']),
    attendee() {
      return this.plan.participants;
    },
    selectedMeetingPlans() {
      return this.getSelectedPlans.filter(plan => plan.type === planTypes.ZOOM_MEETING);
    },

    msqReadOnlyCycle() {
      return this.ctx.supportMSQ && this.ctx.currentFilterPlans.length <= 1;
    },
    removeDisabledReason() {
      const plan = this.selectedMeetingPlans.find(p => p.plan_code !== this.plan.plan_code);
      if (!plan) return '';
      const disableReason = this.ctx.getBillingCycleDisabledDesc(plan?.billing_cycle, true);
      return disableReason;
    },
    currentMeetingPlanLicense() {
      return this.plan.cartInfo.selected_number;
    },
    currentMeetingPlanBillingCycle() {
      return this.plan.billing_cycle;
    },
    billingCycleLabel() {
      return this.$t(`billing.billing_cycle.${this.form.billingCycle}`);
    }
  },
  methods: {
    ...mapMutations(['removePlan', 'updateSelectPlan']),
    ...mapActions(['switchBillingCycleAction']),
    addLargeMeetingCard() {
      this.attendeeInfoTooltip = false
      this.$core.addon(planTypes[planTypes.LARGE_MEETING])
    },
    updatePlanLicense: throttle(function (v) {
      this.$nextTick(async () => {
        if (!this.plan) {
          return;
        }

        let number = v;

        if (!v) {
          number = Math.max(this.plan?.cartInfo?.selected_number ?? 0, this.minHost);
        }

        this.updateSelectPlan({
          ...this.plan,
          cartInfo: {
            selected_number: number,
            add_time: this.plan.cartInfo.add_time,
          },
        });
      });
    }, 200),

    async switchBillingCycle(oldPlan, newPlan) {
      if (!this.plan) {
        return;
      }

      const cartInfo = cloneDeep(oldPlan.cartInfo);
      await this.switchBillingCycleAction([oldPlan?.plan_code, {
        ...newPlan,
        cartInfo,
      }]);

      // this.updateForm();
      // TODO: switch other plan cycle for soft bundle
      // this.switchOtherPlanCycle4SoftBundle(this.form.billingCycle);
    },
    cycleDisabled(planItem) {
      if (planItem.billing_cycle === this.plan.billing_cycle) {
        return false;
      }
      const hasSelectedPlan = this.selectedMeetingPlans.find(plan => plan.billing_cycle === planItem.billing_cycle);
      if (hasSelectedPlan) {
        return true
      }
      return this.ctx.billingCycleDisabled(planItem) || false;
    },
    removeThisPlan() {
      this.removePlan(this.plan.plan_code);
    },
    getMonthlyPrice(amount, cycleValue, options = {}) {
      return this.$money(new BigJs(amount).div(cycleValue).toNumber(), options);
    }
  },
  watch: {
    currentMeetingPlanLicense: {
      immediate: true,
      handler(val) {
        this.form.selectedNumber = val;
      }
    },
    currentMeetingPlanBillingCycle: {
      immediate: true,
      handler(val) {
        this.form.billingCycle = val;
      }
    }
  }
};
</script>
<style lang="scss">
.opc-config-form__multiple-meeting {
  padding: 16px 0;
  border-bottom: 1px solid var(--zoom-color-border-subtle-neutral, #DFE3E8);
  &.hide-bottom-border-last-item:last-of-type {
    border-bottom: none;
  }
}
.opc-msq-config-form {
  .opc-zmone__form-label {
    color: var(--zoom-color-text-stronger-neutral, #00031F);
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 150%; /* 24px */
  }
  .opc-zmone__quantity-selector {
    margin-top: 0;
  }
  .move-to-right {
    float: right;
  }
  .zbo-form-item__label {
    color: var(--zoom-color-text-stronger-neutral, #00031F);
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 150%; /* 24px */
  }
}

.msq-readonly-cycle-content {
  .msq-readonly-cycle-content__title {
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px; /* 128.571% */
    letter-spacing: -0.15px;
    margin-bottom: 5px;
  }
  .msq-readonly-cycle-content__price {
    color: var(--zoom-color-text-neutral, #686F79);
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px; /* 133.333% */
  }
}
.opc-zmone__license-row-right-btn-container {
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
